// src/features/plan/MarketRecordDrawer.jsx
//
// PLACES-MARKETS-DRAWER (task #9053, amendment Corey 2026-10-31).
// Right-side market record drawer opened from the Places Markets list. Replaces
// the inline accordion panes and the routed market landing page as the active
// experience. Composition mirrors ProjectRecordDrawer (SlideSheet + PrevNextPager
// + dialog/focus/dirty contract) without importing the project domain record.
//
// Contracts:
//   - URL: `?peek={marketId}` (+ optional `peekTab=plan|capacity`), written
//     replace-style by MarketPlanPage; helpers below never drop unrelated params.
//   - Header strip is the ONLY market summary. People / usable / installed and
//     First issue come from the Capacity read model through the exact shared
//     Capacity helpers (buildPeriods/evaluateGrid/rowSummary/STATUS_LABEL);
//     no parallel math. Missing/failed values render honestly, never zero.
//   - Market saves (four persisted fields via updatePlanMarket) and office
//     saves (existing OfficeScopeDrawer via OfficesTable) use separate
//     callbacks — see .agents/memory/office-drawer-scope-separation.md.
//   - Lifecycle (deactivate/reactivate/delete) is admin-only.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { T, LAYERS, STAGES } from "../../app/tokens.js";
import { SlideSheet } from "../../components/SlideSheet.jsx";
import { PrevNextPager } from "../../components/PrevNextPager.jsx";
import { stackedDialogOpen } from "../projects/ProjectRecordDrawer.jsx";
import { useSensitivityPolicy } from "../../contexts/SensitivityPolicyContext.jsx";
import { getPlanUsers, updatePlanMarket } from "../../data/marketsClient.js";
import { canonicalMarketOffices, countedMarketOffices } from "../places/marketInventory.js";
import { LocationDrawerV2 } from "../../v2/locations/LocationDrawerV2.jsx";
import { countMarketPlanFields, PLAN_FIELD_TOTAL } from "./PlanCompletenessCell.jsx";
import {
  buildPeriods, evaluateGrid, rowSummary, commitmentsForPeriod, commitmentLabel,
   monthLabel, STATUS_LABEL, DEFAULT_TARGET_BUFFER_PCT, disclosureLines,
} from "../capacity/capacityModel.js";
import { cellStyleFor, CELL_GAP, CELL_RADIUS } from "../capacity/capacityStyles.js";
import { parseCapacityState, serializeCapacityState } from "../capacity/capacityUrl.js";
import {
  StrategyPill, OfficesTable, CandidatesSection, MarketHeader, NextDecisionCell,
  AnnualCostCell, DispositionBadge, fmtDateShort,
  MARKET_PLAN_STRATEGY_OPTIONS, marketPlanEditorValues, marketPlanSavePayload,
} from "./MarketPlanPage.jsx";
import { useEditablePanel } from "../../hooks/useEditablePanel.js";

// Strategy vocabulary is the shared server write list, never a copy.
const strategyLabel = (v) => (MARKET_PLAN_STRATEGY_OPTIONS ?? []).find((o) => o.value === v)?.label;

// ─── Constants ────────────────────────────────────────────────────────────────
export const MARKET_DRAWER_TABS = Object.freeze([
  { key: "overview", label: "Overview" },
  { key: "plan",     label: "Plan" },
  { key: "capacity", label: "Capacity" },
]);
const TAB_KEYS = MARKET_DRAWER_TABS.map((t) => t.key);
export const DRAWER_MIN_WIDTH = 680;
const NARROW_BELOW = 720;
const PANEL_SEL = "[data-testid='market-record-drawer']";
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const IDLE = Object.freeze({ editing: false, busy: false });
const btn = { border: `1px solid ${T.border}`, background: T.card, borderRadius: 8, padding: "5px 11px", fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, cursor: "pointer", fontFamily: "inherit" };
const primaryBtn = { ...btn, background: T.blue, color: T.white, border: `1px solid ${T.blue}` };
const sectionLabel = { fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".06em", textTransform: "uppercase", color: T.mid };
const card = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 12 };

// ─── Pure helpers (exported for tests) ────────────────────────────────────────

/** Read drawer state from a search string. Unknown tabs fall back to overview. */
export function parseMarketPeek(search = "") {
  const p = new URLSearchParams(search);
  const peek = p.get("peek") || null;
  const rawTab = p.get("peekTab");
  return { peek, tab: TAB_KEYS.includes(rawTab) ? rawTab : "overview" };
}

/**
 * Write (or clear, when id is null) peek/peekTab into a search string,
 * preserving every other param. Legacy one-shot `expandMarket` is consumed.
 * Returns the query without a leading "?".
 */
export function withMarketPeek(search = "", id = null, tab = "overview") {
  const p = new URLSearchParams(search);
  p.delete("expandMarket");
  if (id) {
    p.set("peek", id);
    if (tab && tab !== "overview" && TAB_KEYS.includes(tab)) p.set("peekTab", tab); else p.delete("peekTab");
  } else {
    p.delete("peek");
    p.delete("peekTab");
  }
  return p.toString();
}

/** Desktop min 680; narrow screens contain the sheet to the viewport. */
export function drawerWidth(viewport) {
  if (!Number.isFinite(viewport)) return DRAWER_MIN_WIDTH;
  return viewport < NARROW_BELOW ? viewport : DRAWER_MIN_WIDTH;
}

/** Pure close/switch decision, matching ProjectRecordDrawer. */
export function marketDrawerCloseDecision({ editing = false, busy = false } = {}) {
  if (busy) return "wait";
  if (editing) return "confirm";
  return "close";
}

/** Capacity deep link focused/opened on one market (existing URL contract). */
export function capacityHrefForMarket(marketId) {
  return `/places/capacity${serializeCapacityState({ ...parseCapacityState(""), open: [marketId] })}`;
}

/**
 * Baseline Capacity summary for one market — the same 24-month monthly
 * evaluation the Capacity lens runs (buildPeriods + evaluateGrid + rowSummary).
 * status: "loading" | "error" | "excluded" | "ok".
 */
export function marketCapacitySummary(capacity, marketId, bufferPct = DEFAULT_TARGET_BUFFER_PCT) {
  if (!capacity || capacity.status === "idle" || capacity.status === "loading") return { status: "loading" };
  if (capacity.status === "error") return { status: "error", error: capacity.error };
  if (!Array.isArray(capacity.data?.months) || !capacity.data.months.length || !Array.isArray(capacity.data?.markets)) {
    return { status: "error", error: "Capacity response was incomplete." };
  }
  const months = capacity.data?.months ?? [];
  const market = (capacity.data?.markets ?? []).find((m) => m.id === marketId);
  if (!market) return { status: "excluded" };
  const periods = buildPeriods(months, "monthly");
  const [row] = evaluateGrid([market], periods, months, null, bufferPct);
  const { firstIssue } = rowSummary(row, false);
  const fin = (n) => typeof n === "number" && Number.isFinite(n);
  return {
    status: "ok", market, months, periods, cells: row.baseline,
    people: fin(row.baseline[0]?.demand) ? Math.round(row.baseline[0].demand) : null,
    usable: market.months?.[0]?.missingCapacity ? null : fin(row.baseline[0]?.seats) ? Math.round(row.baseline[0].seats) : null,
    installed: market.months?.[0]?.missingCapacity ? null : fin(row.baseline[0]?.installedSeats) ? Math.round(row.baseline[0].installedSeats) : null,
    firstIssue: firstIssue ? { ...firstIssue, label: STATUS_LABEL[firstIssue.status], period: periods[firstIssue.idx]?.full ?? null } : null,
  };
}

/** Lifecycle dates are evidence, not extra seat deltas. Coalesce a same-day
 * schedule change and lifecycle milestone instead of counting them twice. */
export function upcomingCommitments(summary, locations = []) {
  if (summary?.status !== "ok") return [];
  const first = String(summary.months[0]?.start ?? "").slice(0, 10);
  const last = String(summary.months.at(-1)?.start ?? "").slice(0, 7);
  const inHorizon = (date) => /^\d{4}-\d{2}-\d{2}$/.test(date) && date >= first && date.slice(0, 7) <= last;
  const changes = (summary.market.commitments ?? [])
    .filter((c) => Number.isFinite(c.delta) && c.delta !== 0 && inHorizon(String(c.effectiveDate).slice(0, 10)))
    .map((c) => ({ ...c }));
  for (const loc of locations) {
    for (const [raw, kind] of [[loc.onlineDate, "Opening"], [loc.closeDate, "Exit"]]) {
      const date = String(raw ?? "").slice(0, 10);
      if (!inHorizon(date)) continue;
      const matching = changes.find((c) => c.locationId === loc.locationId && c.effectiveDate.slice(0, 10) === date);
      if (matching) matching.eventKind = kind;
      else changes.push({
        id: `${loc.locationId}-${kind}-${date}`, locationId: loc.locationId,
        effectiveDate: date, eventKind: kind, delta: null,
        label: loc.locationAlias || loc.locationId,
      });
    }
  }
  return changes.sort((a, b) => String(a.effectiveDate).localeCompare(String(b.effectiveDate)));
}

/**
 * Office rows for the drawer: membership and counts come ONLY from the
 * canonical inventory (canonicalMarketOffices / countedMarketOffices); plan
 * fields (disposition, target date, notes) are merged by locationId from the
 * plan payload. Inventory loading/failure is reported, never shown as empty.
 */
export function drawerOfficeRows(inventory, market) {
  if (!inventory || inventory.loading) return { status: "loading", rows: [], count: null };
  if (inventory.error) return { status: "error", error: inventory.error, rows: [], count: null };
  const plan = new Map((market?.locations ?? []).map((l) => [l.locationId, l]));
  const rows = canonicalMarketOffices(inventory.locations, market?.id).map((inv) => {
    const p = plan.get(inv.id) ?? {};
    return {
      ...inv, ...p,
      locationId: inv.id,
      locationAlias: inv.locationAlias ?? p.locationAlias,
      stage: inv.stage,
      isHub: inv.isHub ?? p.isHub,
      installedCapacity: inv.installedCapacity ?? p.installedCapacity ?? null,
      headcount: p.headcount ?? inv.latestHeadcount ?? null,
      leaseExpiry: p.leaseExpiry ?? inv.leaseExpiry ?? null,
      inventoryRow: inv,
    };
  });
  return { status: "ok", rows, count: countedMarketOffices(inventory.locations, market?.id).length };
}

// ─── Small presentational pieces ──────────────────────────────────────────────

function NotSet() {
  return <span data-testid="plan-not-set" style={{ color: T.ragAmberText, fontWeight: T.fwSemibold }}>Not set</span>;
}

function StripCell({ label, sub, testId, children }) {
  return (
    <div data-testid={testId} style={{ padding: "10px 14px", minWidth: 0, borderLeft: `1px solid ${T.hair}` }}>
      <div style={sectionLabel}>{label}</div>
      <div style={{ marginTop: 4, fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark, minWidth: 0 }}>{children}</div>
      {sub && <div style={{ marginTop: 2, fontSize: T.fsXxs, color: T.mid }}>{sub}</div>}
    </div>
  );
}

function CapacityUnavailable({ summary, onRetry }) {
  if (summary.status === "loading") {
    return <div data-testid="capacity-strip-loading" aria-busy="true" style={{ height: 22, borderRadius: 6, background: T.lightGray }} />;
  }
  if (summary.status === "error") {
    return (
      <div role="alert" data-testid="capacity-unavailable-error" style={{ display: "flex", gap: 10, alignItems: "center", fontSize: T.fsSm, color: T.mid }}>
        <span style={{ flex: 1 }}>Capacity is unavailable: {summary.error || "the capacity read failed."}</span>
        {onRetry && <button type="button" style={btn} onClick={onRetry} data-testid="capacity-retry">Retry</button>}
      </div>
    );
  }
  return (
    <div data-testid="capacity-unavailable-excluded" style={{ fontSize: T.fsSm, color: T.mid, fontStyle: "italic" }}>
      This market is not in the Capacity model (no eligible active offices). Installed-seat figures are not substituted.
    </div>
  );
}

/** Shared 24-month baseline strip — same squares, colours and commitment marks as Capacity. */
export function CapacityStrip({ summary, testId = "capacity-strip" }) {
  const size = 18;
  return (
    <div data-testid={testId} role="img" aria-label="24-month capacity status, baseline"
      style={{ display: "flex", gap: CELL_GAP, flexWrap: "wrap" }}>
      {summary.periods.map((p, i) => {
        const c = summary.cells[i];
        const st = cellStyleFor(c);
        const marks = commitmentsForPeriod(summary.market, p, summary.months);
        return (
          <div key={p.key} data-testid={`${testId}-cell-${p.key}`} data-status={c?.status}
            title={`${p.full}: ${c?.demand == null ? "–" : Math.round(c.demand)} / ${c?.seats == null ? "–" : Math.round(c.seats)} usable · ${STATUS_LABEL[c?.status] ?? "No data"} · ${[...disclosureLines(c), ...marks.map(commitmentLabel)].join("; ")}`}
            style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
            <span style={{
              width: size, height: size, borderRadius: CELL_RADIUS, background: st.bg, boxSizing: "border-box",
              border: `1px ${st.dashed ? "dashed" : "solid"} ${st.border}`, position: "relative",
            }}>
              {marks.length > 0 && (
                <span data-testid={`${testId}-mark-${p.key}`} aria-hidden="true" style={{
                  position: "absolute", top: -3, right: -3, width: 6, height: 6, borderRadius: "50%",
                  background: T.dark, boxShadow: `0 0 0 1.5px ${T.card}`,
                }} />
              )}
            </span>
            <span style={{ fontSize: T.fsXxxs, color: p.yearStart ? T.dark : T.light, fontWeight: p.yearStart ? T.fwBold : T.fwSemibold }}>{p.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function CapacityNotes({ summary }) {
  const lines = [...new Set((summary.cells ?? []).flatMap(disclosureLines))];
  return <div data-testid="drawer-capacity-notes" style={{ marginTop: 8, fontSize: T.fsXs, color: T.mid }}>
    {lines.map((line) => <div key={line}>{line}</div>)}
  </div>;
}

// ─── Header ───────────────────────────────────────────────────────────────────

function DrawerHeader({ market, summary, canSeeCost, officeCount, hasHub, planCount, pager }) {
  const issue = summary.status === "ok" ? summary.firstIssue : null;
  return (
    <header style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, paddingRight: 36 }}>
        <div style={{ minWidth: 0 }}>
          <div style={sectionLabel}>Market</div>
          <h2 id="market-drawer-title" tabIndex={-1} data-testid="market-drawer-title"
            style={{ margin: "2px 0 6px", fontSize: T.fsLg, fontWeight: T.fwHubTitle ?? T.fwBold, color: T.dark, outline: "none", overflowWrap: "anywhere" }}>
            {market.name}
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: T.fsXs, color: T.mid }}>
            {hasHub && (
              <span data-testid="market-drawer-hub" style={{ background: T.hubChipBg, border: `1px solid ${T.hubChipBorder}`, color: T.hubChipText, fontSize: T.fsXxxs, fontWeight: T.fwBold, padding: "1px 5px", borderRadius: 3 }}>Hub</span>
            )}
            {market.strategy && <StrategyPill strategy={market.strategy} />}
            <span>{market.region || "No region"}</span>
            <span aria-hidden="true">·</span>
            <span data-testid="market-drawer-office-count">{officeCount == null ? "Offices unavailable" : `${officeCount} ${officeCount === 1 ? "office" : "offices"}`}</span>
            <span aria-hidden="true">·</span>
            <span data-testid="market-drawer-plan-count" style={{ color: planCount === PLAN_FIELD_TOTAL ? T.greenDeep : T.mid, fontWeight: T.fwSemibold }}>
              Plan {planCount} of {PLAN_FIELD_TOTAL} set
            </span>
          </div>
        </div>
        {pager}
      </div>

      <div data-testid="market-drawer-strip" style={{ ...card, marginTop: 12, display: "grid", gridTemplateColumns: canSeeCost ? "repeat(4, minmax(0,1fr))" : "repeat(3, minmax(0,1fr))", overflow: "hidden" }}>
        <StripCell testId="strip-people-seats" label="People / seats"
          sub={summary.status === "ok" ? (summary.installed != null ? `${summary.installed} installed` : "installed unknown") : summary.status === "loading" ? null : "Capacity unavailable"}>
          {summary.status === "ok"
            ? <span>{summary.people ?? "\u2013"} / {summary.usable ?? "\u2013"} <span style={{ fontWeight: T.fwSemibold, color: T.mid, fontSize: T.fsXs }}>usable</span></span>
            : summary.status === "loading" ? <span aria-busy="true" style={{ display: "inline-block", width: 80, height: 14, background: T.lightGray, borderRadius: 4 }} /> : <span style={{ color: T.light }}>{"\u2013"}</span>}
        </StripCell>
        <StripCell testId="strip-first-issue" label="First issue"
          sub={issue ? issue.period : summary.status === "ok" ? "next 24 months" : null}>
          {summary.status !== "ok"
            ? <span style={{ color: T.light }}>{"\u2013"}</span>
            : issue
              ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: cellStyleFor({ status: issue.status }).bg }} />
                  {issue.label}
                </span>
              : <span style={{ color: summary.cells.every((c) => c?.status === "no_data") ? T.mid : T.greenDeep }}>
                  {summary.cells.every((c) => c?.status === "no_data") ? "No data" : "None in 24 mo"}
                </span>}
        </StripCell>
        <StripCell testId="strip-next-decision" label="Next decision">
          <NextDecisionCell market={market} />
        </StripCell>
        {canSeeCost && (
          <StripCell testId="strip-annual-cost" label="Annual cost" sub="all-in (rent + opex)">
            <AnnualCostCell totalAnnualCost={market.totalAnnualCost} rentIncompleteOffices={market.rentIncompleteOffices ?? []} isCompact />
          </StripCell>
        )}
      </div>
    </header>
  );
}

// ─── Overview ─────────────────────────────────────────────────────────────────

function OfficePlanValue({ loc, canEdit, onEditOfficePlan }) {
  const has = loc.disposition || loc.targetActionDate;
  const eligible = loc.stage !== "vacated" && loc.disposition !== "exit" &&
    loc.leaseStatus !== "vacant_held" && loc.leaseStatus !== "Vacant (held)";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      {has ? (
        <>
          {loc.disposition ? <DispositionBadge disposition={loc.disposition} /> : <NotSet />}
          <span style={{ fontSize: T.fsXs, color: T.mid }}>{loc.targetActionDate ? fmtDateShort(loc.targetActionDate) : "no date"}</span>
        </>
      ) : <span style={{ fontSize: T.fsXs, color: T.light }}>Set plan</span>}
      {canEdit && eligible && (
        <button type="button" data-testid={`overview-edit-office-plan-${loc.locationId}`} onClick={() => onEditOfficePlan(loc)}
          aria-label={`${has ? "Edit" : "Set"} plan for ${loc.locationAlias || loc.locationId}`}
          style={{ ...btn, padding: "2px 8px", fontSize: T.fsXxs }}>{has ? "Edit" : "Set plan"}</button>
      )}
    </span>
  );
}

function OverviewTab({ market, officeState, onRetryInventory, summary, canEdit, onPeekOffice, onEditOfficePlan, onDetails, onOpenInOffices, onRetryCapacity }) {
  const th = { ...sectionLabel, textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" };
  const td = { padding: "9px 10px", borderBottom: `1px solid ${T.hair}`, fontSize: T.fsSm, color: T.dark, verticalAlign: "middle" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <section aria-label="Offices" style={{ ...card, padding: "12px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>Offices</div>
          <button type="button" style={btn} data-testid="open-in-offices" onClick={onOpenInOffices}>Open in Offices</button>
        </div>
        {officeState.status === "loading" ? (
          <div data-testid="overview-offices-loading" aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: 6, padding: "6px 0" }}>
            {[0, 1, 2].map((i) => <div key={i} style={{ height: 28, borderRadius: 6, background: T.lightGray }} />)}
          </div>
        ) : officeState.status === "error" ? (
          <div role="alert" data-testid="overview-offices-error" style={{ display: "flex", gap: 10, alignItems: "center", fontSize: T.fsSm, color: T.mid, padding: "8px 0" }}>
            <span style={{ flex: 1 }}>Office inventory is unavailable: {officeState.error}</span>
            {onRetryInventory && <button type="button" style={btn} onClick={onRetryInventory} data-testid="overview-offices-retry">Retry</button>}
          </div>
        ) : officeState.rows.length === 0 ? (
          <div data-testid="overview-offices-empty" style={{ padding: "14px 0", fontSize: T.fsSm, color: T.mid }}>
            No offices are assigned to this market yet. Move an office here from its current market's Plan tab, or from Unassigned.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table data-testid="overview-offices-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th}>Office</th><th style={th}>Stage</th><th style={{ ...th, textAlign: "right" }}>Seats</th>
                <th style={{ ...th, textAlign: "right" }}>People</th><th style={th}>Lease to</th><th style={th}>In the plan</th>
              </tr></thead>
              <tbody>
                {officeState.rows.map((loc) => (
                  <tr key={loc.locationId} data-testid={`overview-office-${loc.locationId}`}
                    onClick={(e) => { if (!e.target.closest("button")) onPeekOffice(loc, e.currentTarget); }}
                    style={{ cursor: "pointer" }}>
                    <td style={td}>
                      <button type="button" data-testid={`overview-open-office-${loc.locationId}`}
                        onClick={(e) => onPeekOffice(loc, e.currentTarget)}
                        aria-label={`Open office ${loc.locationAlias || loc.locationId}`}
                        style={{ border: 0, background: "transparent", padding: 0, textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
                        <div style={{ fontWeight: T.fwSemibold, color: T.blue }}>{loc.locationAlias || loc.name || loc.locationId}</div>
                        <div style={{ fontSize: T.fsXxs, color: T.light }}>{loc.locationId}</div>
                      </button>
                    </td>
                    <td style={td}>{STAGES[loc.stage]?.label ?? (loc.stage === "closed" ? "Retired" : loc.stage ?? "\u2013")}</td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{loc.installedCapacity ?? "\u2013"}</td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{loc.headcount ?? "\u2013"}</td>
                    <td style={td}>{loc.leaseExpiry ? fmtDateShort(loc.leaseExpiry) : "\u2013"}</td>
                    <td style={td}><OfficePlanValue loc={loc} canEdit={canEdit} onEditOfficePlan={onEditOfficePlan} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section aria-label="Capacity outlook" style={{ ...card, padding: "12px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>Capacity · next 24 months</div>
          <button type="button" style={btn} data-testid="overview-capacity-details" onClick={onDetails}>Details</button>
        </div>
        {summary.status === "ok" ? <><CapacityStrip summary={summary} testId="overview-capacity-strip" /><CapacityNotes summary={summary} /></>
          : <CapacityUnavailable summary={summary} onRetry={onRetryCapacity} />}
      </section>
    </div>
  );
}

// ─── Plan ─────────────────────────────────────────────────────────────────────

function PlanReadBox({ label, testId, children }) {
  return (
    <div data-testid={testId} style={{ ...card, padding: "10px 12px", minWidth: 0 }}>
      <div style={sectionLabel}>{label}</div>
      <div style={{ marginTop: 4, fontSize: T.fsSm, color: T.dark, overflowWrap: "anywhere" }}>{children}</div>
    </div>
  );
}

/** Inline editor for the four persisted market fields: the existing
 *  MarketPlanDrawer contract (marketPlanEditorValues / marketPlanSavePayload /
 *  STRATEGY_OPTIONS / getPlanUsers) under the canonical useEditablePanel. */
function MarketPlanInlineEditor({ market, onCancel, onSaved, onDirtyChange, onBusyChange }) {
  const saved = marketPlanEditorValues(market);
  const panel = useEditablePanel(() => marketPlanEditorValues(market));
  const { draft, setField, setEditing, reset } = panel;
  const [users, setUsers] = useState([]);
  const [usersError, setUsersError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { setEditing(true); }, [setEditing]);
  const dirty = panel.editing && Object.keys(saved).some((k) => (draft[k] ?? "") !== (saved[k] ?? ""));
  const dirtyRef = useRef(onDirtyChange); dirtyRef.current = onDirtyChange;
  const busyRef = useRef(onBusyChange); busyRef.current = onBusyChange;
  useEffect(() => { dirtyRef.current?.(dirty); }, [dirty]);
  useEffect(() => { busyRef.current?.(saving); }, [saving]);
  useEffect(() => () => { dirtyRef.current?.(false); busyRef.current?.(false); }, []);
  useEffect(() => {
    let cancelled = false;
    getPlanUsers().then((l) => {
      if (!Array.isArray(l)) throw new Error("Owner list response was malformed");
      if (!cancelled) setUsers(l);
    }).catch((err) => { if (!cancelled) setUsersError(err?.message ?? "Owner list unavailable"); });
    return () => { cancelled = true; };
  }, []);
  const owners = useMemo(() => {
    const names = users.map((u) => u.displayName).filter(Boolean);
    if (draft.owner && !names.includes(draft.owner)) names.unshift(draft.owner);
    return names;
  }, [users, draft.owner]);
  async function save() {
    setSaving(true); setError(null);
    try {
      const { patch, before } = marketPlanSavePayload(market, draft);
      const updated = await updatePlanMarket(market.id, patch, before);
      dirtyRef.current?.(false);
      reset();
      await onSaved(market.id, updated, patch);
    } catch (err) {
      // Server wording is shown verbatim; the draft is kept for retry.
      setError(err?.message || "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }
  const set = (k) => (e) => setField(k, e.target.value);
  const input = { width: "100%", boxSizing: "border-box", border: `1px solid ${T.border}`, borderRadius: 7, padding: "7px 9px", fontSize: T.fsSm, fontFamily: "inherit", background: T.card, color: T.dark };
  return (
    <form data-testid="market-plan-inline-editor" onSubmit={(e) => { e.preventDefault(); if (dirty && !saving) save(); }}
      style={{ ...card, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        <label style={sectionLabel}>Strategy
          <select data-testid="inline-strategy" value={draft.strategy ?? ""} onChange={set("strategy")} style={{ ...input, marginTop: 4 }}>
            <option value="">Not set</option>
            {MARKET_PLAN_STRATEGY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label style={sectionLabel}>Target date
          <input data-testid="inline-target-date" type="date" value={draft.strategyTargetDate ?? ""} onChange={set("strategyTargetDate")} style={{ ...input, marginTop: 4 }} />
        </label>
        <label style={sectionLabel}>Owner
          <select data-testid="inline-owner" value={draft.owner ?? ""} onChange={set("owner")} style={{ ...input, marginTop: 4 }}>
            <option value="">Not set</option>
            {owners.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
      <label style={sectionLabel}>Note
        <textarea data-testid="inline-note" rows={3} value={draft.planNote ?? ""} onChange={set("planNote")} style={{ ...input, marginTop: 4, resize: "vertical", textTransform: "none", letterSpacing: 0 }} />
      </label>
      {usersError && <div role="alert" data-testid="inline-owner-error" style={{ color: T.ragAmberText, fontSize: T.fsSm }}>{usersError}</div>}
      {error && <div role="alert" data-testid="inline-plan-error" style={{ color: T.ragRed, fontSize: T.fsSm }}>{error}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" style={btn} onClick={() => { reset(); onCancel(); }} disabled={saving} data-testid="inline-plan-cancel">Cancel</button>
        <button type="submit" style={{ ...primaryBtn, opacity: !dirty || saving ? 0.55 : 1 }} disabled={!dirty || saving} data-testid="inline-plan-save">
          {saving ? "Saving" : "Save plan"}
        </button>
      </div>
    </form>
  );
}

function PlanTab({
  market, locations, officeState, canEdit, canManageLifecycle, editing, setEditing, onMarketSaved, onOfficeSaved,
  onOpenLocation, onMoveOffice, allMarkets, openOfficeId, setOpenOfficeId, onDeleteMarket,
  onStartWorkflow, onOpenLinkedRun, onPlanDirty, onPlanBusy,
  onOfficeBusy, onCandidateState,
}) {
  const exited = market.status === "Exited" || market.active === false;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <section aria-label="Market plan" style={{ ...card, paddingBottom: 14, overflow: "visible" }}>
        <MarketHeader
          market={market} canEdit={canEdit} canManageLifecycle={canManageLifecycle} title="Market plan" hideEditPlan
          onSaved={onMarketSaved} onDeleteMarket={onDeleteMarket}
          onStartWorkflow={onStartWorkflow} onOpenLinkedRun={onOpenLinkedRun}
        />
        <div style={{ padding: "12px 20px 0" }}>
          {editing ? (
            <MarketPlanInlineEditor market={market} onCancel={() => setEditing(false)}
              onDirtyChange={onPlanDirty} onBusyChange={onPlanBusy}
              onSaved={async (...args) => { setEditing(false); await onMarketSaved(...args); }} />
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8 }}>
                <PlanReadBox label="Strategy" testId="plan-read-strategy">{market.strategy ? strategyLabel(market.strategy) ?? market.strategy : <NotSet />}</PlanReadBox>
                <PlanReadBox label="Target date" testId="plan-read-target">{market.strategyTargetDate ? fmtDateShort(market.strategyTargetDate) : <NotSet />}</PlanReadBox>
                <PlanReadBox label="Owner" testId="plan-read-owner">{market.owner || <NotSet />}</PlanReadBox>
                <PlanReadBox label="Next decision" testId="plan-read-next-decision"><NextDecisionCell market={market} /></PlanReadBox>
              </div>
              <div style={{ marginTop: 8 }}>
                <PlanReadBox label="Note" testId="plan-read-note">
                  {market.planNote ? <span style={{ whiteSpace: "pre-wrap" }}>{market.planNote}</span> : <NotSet />}
                </PlanReadBox>
              </div>
              {canEdit && !exited && (
                <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
                  <button type="button" style={primaryBtn} data-testid="drawer-edit-plan" onClick={() => setEditing(true)}>Edit plan</button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section aria-label="Each office in the plan" data-testid="plan-offices" style={{ ...card, overflow: "hidden" }}>
        <div style={{ padding: "12px 16px 0", fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>Each office in the plan</div>
        {officeState.status !== "ok" ? (
          <div data-testid="plan-offices-unavailable" role={officeState.status === "error" ? "alert" : undefined} aria-busy={officeState.status === "loading"}
            style={{ padding: "12px 16px", fontSize: T.fsSm, color: T.mid }}>
            {officeState.status === "error" ? `Office inventory is unavailable: ${officeState.error}` : "Loading offices"}
          </div>
        ) : <OfficesTable
          recordMode
          locations={locations}
          onOpenLocation={onOpenLocation}
          canEdit={canEdit}
          onMoveOffice={(loc) => { setOpenOfficeId(null); onMoveOffice?.(loc); }}
          currentMarketId={market.id}
          allMarkets={allMarkets}
          onOpenOfficeDrawer={(loc) => setOpenOfficeId(loc.locationId)}
          openOfficeId={openOfficeId}
          onCloseOffice={() => setOpenOfficeId(null)}
          onOfficeSaved={(locationId, updated, patch) => onOfficeSaved(market.id, locationId, updated, patch)}
          onOfficeBusy={onOfficeBusy}
        />}
      </section>

      <section aria-label="Candidates" data-testid="plan-candidates" style={{ ...card, overflow: "hidden" }}>
        <CandidatesSection marketId={market.id} canEdit={canEdit} onEditorState={onCandidateState} />
      </section>
    </div>
  );
}

// ─── Capacity tab ─────────────────────────────────────────────────────────────

function CapacityTab({ market, summary, onRetry, capacityHref, onOpenCapacity }) {
  const changes = upcomingCommitments(summary, market.locations ?? []);
  const offices = new Map((market.locations ?? []).map((l) => [l.locationId, l]));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <section aria-label="Capacity outlook" style={{ ...card, padding: "12px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 8 }}>
          <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>Baseline · next 24 months</div>
          <a href={capacityHref} data-testid="open-in-capacity" onClick={(e) => { if (onOpenCapacity) { e.preventDefault(); onOpenCapacity(); } }}
            style={{ ...btn, textDecoration: "none" }}>Open in Capacity</a>
        </div>
        {summary.status === "ok" ? <><CapacityStrip summary={summary} testId="capacity-tab-strip" /><CapacityNotes summary={summary} /></>
          : <CapacityUnavailable summary={summary} onRetry={onRetry} />}
      </section>
      <section aria-label="Scheduled capacity changes" style={{ ...card, padding: "12px 14px" }}>
        <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark, marginBottom: 8 }}>Scheduled capacity changes</div>
        {summary.status !== "ok" ? (
          <div style={{ fontSize: T.fsSm, color: T.mid }}>Unavailable while Capacity data is unavailable.</div>
        ) : changes.length === 0 ? (
          <div data-testid="capacity-changes-empty" style={{ fontSize: T.fsSm, color: T.mid }}>
            No dated capacity changes, openings or exits are recorded in the next 24 months.
          </div>
        ) : (
          <ul data-testid="capacity-changes" style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {changes.map((c) => {
              const loc = offices.get(c.locationId);
              return (
                <li key={c.id ?? `${c.locationId}-${c.effectiveDate}`} data-testid={`capacity-change-${c.id ?? c.locationId}`}
                  style={{ display: "grid", gridTemplateColumns: "90px 1fr auto", gap: 10, padding: "8px 0", borderTop: `1px solid ${T.hair}`, fontSize: T.fsSm, alignItems: "baseline" }}>
                  <span style={{ color: T.mid, fontVariantNumeric: "tabular-nums" }}>{monthLabel(c.effectiveDate)}</span>
                  <span style={{ color: T.dark, minWidth: 0 }}>
                    {c.delta == null ? c.label : commitmentLabel(c)}
                    {loc && c.projectName && <span style={{ color: T.light }}> · {loc.locationAlias || loc.locationId}</span>}
                  </span>
                  <span style={{ fontWeight: T.fwBold, color: c.eventKind === "Exit" || c.delta < 0 ? T.ragRed : T.greenDeep }}>
                    {c.eventKind ?? (c.delta > 0 ? "Scheduled add" : "Scheduled reduction")}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

// ─── Drawer host ──────────────────────────────────────────────────────────────

export function MarketRecordDrawer({
  open, marketId, market, tab = "overview", onTabChange, onClose, orderedIds = [], onNavigate,
  currentRole = "viewer", allMarkets = [], capacity, inventory, bufferPct, capacityNote, onRetryCapacity,
  onOpenLocation, onMarketSaved, onOfficeSaved, onMoveOffice, onDeleteMarket,
  onStartWorkflow, onOpenLinkedRun, onOpenInOffices, onOpenCapacity, nestedOpen = false, guardRef,
}) {
  const { canSee } = useSensitivityPolicy();
  const canSeeCost = canSee("market_plan.rent_cost");
  const canEdit = currentRole === "editor" || currentRole === "admin";
  const isAdmin = currentRole === "admin";
  const active = Boolean(open && marketId);
  const bodyRef = useRef(null);
  const returnFocus = useRef(null);
  const [width, setWidth] = useState(() => drawerWidth(typeof window === "undefined" ? 1440 : window.innerWidth));
  const [editingPlan, setEditingPlan] = useState(false);
  const [planDirty, setPlanDirty] = useState(false);
  const [planBusy, setPlanBusy] = useState(false);
  const [officeBusy, setOfficeBusy] = useState(false);
  const [candidateState, setCandidateState] = useState(IDLE);
  const [openOfficeId, setOpenOfficeId] = useState(null);
  const [ask, setAsk] = useState(null);
  const editor = useRef(IDLE);
  editor.current = { editing: planDirty || Boolean(openOfficeId) || candidateState.editing, busy: planBusy || officeBusy || candidateState.busy };
  // Nested office peek: the existing LocationDrawerV2 over the canonical inventory row.
  const [officePeek, setOfficePeek] = useState(null);
  const officeReturn = useRef(null);
  const nestedRef = useRef(nestedOpen); nestedRef.current = nestedOpen || Boolean(officePeek);
  const peekOffice = (loc, trigger) => { officeReturn.current = trigger ?? document.activeElement; setOfficePeek(loc.inventoryRow ?? loc); };
  const closeOfficePeek = () => {
    setOfficePeek(null);
    const el = officeReturn.current;
    setTimeout(() => { if (el && el.isConnected && typeof el.focus === "function") el.focus(); }, 0);
  };
  useEffect(() => {
    if (!officePeek) return undefined;
    const officePanel = () => document.querySelector("[data-testid='market-office-peek']");
    const t = setTimeout(() => {
      const el = officePanel();
      if (el) {
        el.setAttribute("role", "dialog");
        el.setAttribute("aria-modal", "true");
        el.setAttribute("aria-label", `Office ${officePeek.locationAlias || officePeek.id}`);
        el.tabIndex = -1;
        (el.querySelector(FOCUSABLE) ?? el).focus();
      }
    }, 0);
    const trap = (event) => {
      if (event.key !== "Tab") return;
      const el = officePanel();
      if (!el) return;
      const items = [...el.querySelectorAll(FOCUSABLE)].filter((node) => !node.closest("[hidden]"));
      const first = items[0] ?? el, last = items.at(-1) ?? el;
      if (!items.includes(document.activeElement)) {
        event.preventDefault(); (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener("keydown", trap, true);
    return () => { clearTimeout(t); document.removeEventListener("keydown", trap, true); };
  }, [officePeek]);

  // Reset nested state whenever the record changes or the drawer closes.
  useEffect(() => { setEditingPlan(false); setPlanDirty(false); setOfficeBusy(false); setCandidateState(IDLE); setOpenOfficeId(null); setAsk(null); setOfficePeek(null); }, [marketId, open]);

  useEffect(() => {
    if (!guardRef) return undefined;
    guardRef.current = () => (active ? { dirty: editor.current.editing || editor.current.busy, busy: editor.current.busy } : { dirty: false, busy: false });
    return () => { guardRef.current = null; };
  }, [guardRef, active]);

  const panel = () => bodyRef.current?.closest(PANEL_SEL) || null;

  useEffect(() => {
    if (!active) return undefined;
    returnFocus.current = document.activeElement;
    const t = setTimeout(() => {
      const el = panel();
      if (el) { el.setAttribute("role", "dialog"); el.setAttribute("aria-modal", "true"); el.setAttribute("aria-labelledby", "market-drawer-title"); }
      bodyRef.current?.querySelector("[data-testid='market-drawer-title']")?.focus();
    }, 0);
    const onResize = () => setWidth(drawerWidth(window.innerWidth));
    onResize();
    window.addEventListener("resize", onResize);
    const onBefore = (e) => { if (editor.current.editing || editor.current.busy) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onBefore);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("beforeunload", onBefore);
      const el = returnFocus.current;
      setTimeout(() => { if (el && el.isConnected && typeof el.focus === "function") el.focus(); }, 0);
    };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus containment while no stacked dialog owns the keyboard.
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key !== "Tab") return;
      const el = panel();
      if (!el || nestedRef.current || stackedDialogOpen(el)) return;
      const items = [...el.querySelectorAll(FOCUSABLE)].filter((n) => !n.closest("[hidden]"));
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0]; const last = items[items.length - 1];
      const cur = document.activeElement;
      if (!items.includes(cur)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
      else if (e.shiftKey && cur === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && cur === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [active]);

  const resetNested = () => { setEditingPlan(false); setPlanDirty(false); setOpenOfficeId(null); editor.current = IDLE; };
  const forceClose = () => { if (editor.current.busy) return; setAsk(null); resetNested(); onClose(); };
  const requestClose = useCallback(() => {
    if (nestedRef.current || stackedDialogOpen(panel())) return; // stacked dialog owns Escape/scrim
    const d = marketDrawerCloseDecision(editor.current);
    if (d === "close") forceClose();
    else setAsk((a) => a ?? { label: "Discard and close" });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const requestRecord = (target) => {
    if (ask || editor.current.busy || nestedRef.current || stackedDialogOpen(panel())) return;
    if (!orderedIds.includes(target) || !onNavigate) return;
    if (editor.current.editing) setAsk({ target, label: "Discard and switch" });
    else { onNavigate(target); bodyRef.current?.querySelector("[data-testid='market-drawer-title']")?.focus(); }
  };
  const requestLeave = (action) => {
    if (editor.current.editing || editor.current.busy) setAsk({ action, label: "Discard and continue" });
    else action();
  };
  const confirmDiscard = () => {
    if (editor.current.busy) return;
    const intent = ask;
    setAsk(null);
    resetNested();
    if (intent?.action) intent.action();
    else if (intent?.target) { if (orderedIds.includes(intent.target)) onNavigate?.(intent.target); }
    else onClose();
  };

  const summary = useMemo(() => marketCapacitySummary(capacity, marketId, bufferPct), [capacity, marketId, bufferPct]);
  const officeState = useMemo(() => drawerOfficeRows(inventory, market), [inventory, market]);
  const members = officeState.rows;
  const officeCount = officeState.count;
  const hasHub = members.some((l) => l.isHub);
  const busy = planBusy || officeBusy || candidateState.busy;

  const editOfficePlan = (loc) => { onTabChange?.("plan"); setOpenOfficeId(loc.locationId); };

  const pager = (
    <PrevNextPager ids={orderedIds} currentId={marketId} onNavigate={requestRecord}
      keyboardActive={active && !nestedOpen} disabled={!onNavigate || Boolean(ask) || nestedOpen} busy={busy} compact showUnavailable
      previousLabel="Previous market" nextLabel="Next market" previousTestId="market-drawer-prev" nextTestId="market-drawer-next" />
  );

  return createPortal(
    <SlideSheet open={active} onClose={requestClose} width={width} layer={LAYERS.stickyChrome + 1}
      panelTestId="market-record-drawer" scrimTestId="market-record-drawer-scrim" closeTestId="market-record-drawer-close">
      <div ref={bodyRef} data-testid="market-drawer-body" style={{ padding: "18px 22px 40px", boxSizing: "border-box", minHeight: "100%", background: T.tint || T.lightGray }}>
        {!market ? (
          <div data-testid="market-drawer-missing" style={{ paddingRight: 36 }}>
            <div style={sectionLabel}>Market</div>
            <h2 id="market-drawer-title" tabIndex={-1} data-testid="market-drawer-title" style={{ margin: "2px 0 8px", fontSize: T.fsLg, color: T.dark, outline: "none" }}>Market not found</h2>
            <p style={{ fontSize: T.fsSm, color: T.mid }}>No market with ID "{marketId}" is available to you. It may have been deleted, renamed or filtered by permissions.</p>
          </div>
        ) : (
          <>
            <DrawerHeader market={market} summary={summary} canSeeCost={canSeeCost} officeCount={officeCount} hasHub={hasHub}
              planCount={countMarketPlanFields(market)} pager={pager} />
            {!orderedIds.includes(marketId) && (
              <div data-testid="market-drawer-out-of-list" style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 8 }}>
                This market is outside the current filtered list, so Previous / Next are unavailable.
              </div>
            )}

            {ask && (
              <div role="alert" data-testid="market-drawer-unsaved" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", padding: "8px 12px", borderRadius: 8, background: T.utilAmber, fontSize: T.fsSm, marginBottom: 10 }}>
                <span style={{ flex: 1, minWidth: 160 }}>
                  {busy ? "A plan change is still saving. Wait for it to finish before leaving." : "An editor is open. Leaving now discards unsaved changes."}
                </span>
                <button type="button" data-testid="market-drawer-keep" onClick={() => setAsk(null)} style={btn}>Keep editing</button>
                <button type="button" data-testid="market-drawer-discard" disabled={busy} onClick={confirmDiscard} style={{ ...btn, opacity: busy ? 0.5 : 1 }}>{ask.label}</button>
              </div>
            )}

            {capacityNote && <p data-testid="drawer-capacity-policy-note" style={{ color: T.mid, fontSize: T.fsXs }}>{capacityNote}</p>}
            <div role="tablist" aria-label="Market record" style={{ display: "flex", gap: 4, borderBottom: `1px solid ${T.border}`, marginBottom: 14 }}>
              {MARKET_DRAWER_TABS.map((t) => {
                const sel = tab === t.key;
                return (
                  <button key={t.key} type="button" role="tab" id={`market-tab-${t.key}`} aria-selected={sel} aria-controls={`market-tabpanel-${t.key}`}
                    data-testid={`market-drawer-tab-${t.key}`} tabIndex={sel ? 0 : -1}
                    onClick={() => onTabChange?.(t.key)}
                    onKeyDown={(e) => {
                      const i = TAB_KEYS.indexOf(t.key);
                      const next = e.key === "ArrowRight" ? TAB_KEYS[(i + 1) % TAB_KEYS.length]
                        : e.key === "ArrowLeft" ? TAB_KEYS[(i + TAB_KEYS.length - 1) % TAB_KEYS.length]
                        : e.key === "Home" ? TAB_KEYS[0] : e.key === "End" ? TAB_KEYS[TAB_KEYS.length - 1] : null;
                      if (!next) return;
                      e.preventDefault(); e.stopPropagation();
                      onTabChange?.(next);
                      setTimeout(() => document.getElementById(`market-tab-${next}`)?.focus(), 0);
                    }}
                    style={{
                      border: 0, background: "transparent", padding: "8px 12px", cursor: "pointer", fontFamily: "inherit",
                      fontSize: T.fsSm, fontWeight: sel ? T.fwBold : T.fwSemibold, color: sel ? T.dark : T.mid,
                      borderBottom: `2px solid ${sel ? T.dark : "transparent"}`, marginBottom: -1,
                    }}>{t.label}</button>
                );
              })}
            </div>

            {/* All panels stay mounted; inactive ones are hidden so an open Plan or
                office editor keeps its draft across tab switches (dirty guard). */}
            <div role="tabpanel" id="market-tabpanel-overview" aria-labelledby="market-tab-overview" data-testid="market-drawer-panel-overview" hidden={tab !== "overview"}>
              <OverviewTab market={market} officeState={officeState} onRetryInventory={inventory?.reload} summary={summary} canEdit={canEdit}
                onPeekOffice={peekOffice} onEditOfficePlan={editOfficePlan}
                onDetails={() => requestLeave(() => {
                  if (onOpenCapacity) onOpenCapacity(market.id);
                  else window.location.assign(capacityHrefForMarket(market.id));
                })} onOpenInOffices={() => requestLeave(() => onOpenInOffices?.(market))} onRetryCapacity={onRetryCapacity} />
            </div>
            <div role="tabpanel" id="market-tabpanel-plan" aria-labelledby="market-tab-plan" data-testid="market-drawer-panel-plan" hidden={tab !== "plan"}>
              <PlanTab key={market.id} market={market} locations={members} officeState={officeState} canEdit={canEdit} canManageLifecycle={isAdmin}
                editing={editingPlan} setEditing={(v) => { setEditingPlan(v); if (!v) setPlanDirty(false); }}
                onMarketSaved={onMarketSaved} onOfficeSaved={onOfficeSaved} onOpenLocation={(id) => { const row = members.find((m) => m.locationId === id); if (row) peekOffice(row); else onOpenLocation?.(id); }}
                onMoveOffice={onMoveOffice} allMarkets={allMarkets} openOfficeId={openOfficeId} setOpenOfficeId={setOpenOfficeId}
                onDeleteMarket={onDeleteMarket} onStartWorkflow={onStartWorkflow} onOpenLinkedRun={onOpenLinkedRun}
                onPlanDirty={setPlanDirty} onPlanBusy={setPlanBusy} onOfficeBusy={setOfficeBusy} onCandidateState={setCandidateState} />
            </div>
            <div role="tabpanel" id="market-tabpanel-capacity" aria-labelledby="market-tab-capacity" data-testid="market-drawer-panel-capacity" hidden={tab !== "capacity"}>
              <CapacityTab market={{ ...market, locations: members }} summary={summary} onRetry={onRetryCapacity}
                capacityHref={capacityHrefForMarket(market.id)} onOpenCapacity={() => requestLeave(() => {
                  if (onOpenCapacity) onOpenCapacity(market.id);
                  else window.location.assign(capacityHrefForMarket(market.id));
                })} />
            </div>
          </>
        )}
      </div>
      {createPortal(<SlideSheet open={Boolean(officePeek)} onClose={closeOfficePeek} width={Math.min(400, width)} layer={LAYERS.stickyChrome + 2}
        panelTestId="market-office-peek" scrimTestId="market-office-peek-scrim" hideCloseButton>
        <LocationDrawerV2 loc={officePeek} onClose={closeOfficePeek}
          onOpenDetail={(id) => { if (editor.current.editing || editor.current.busy) return; setOfficePeek(null); onOpenLocation?.(id); }} />
      </SlideSheet>, document.body)}
    </SlideSheet>, document.body,
  );
}

export default MarketRecordDrawer;
